/* Slide preview body — thin dispatcher that picks the right per-slide
   preview component. Iter85: the 15+ design-gallery layouts (KPI,
   Timeline, Funnel, Photo variants, Numbered list etc.) all live in
   `slidePreviews/GalleryLayouts.jsx` behind a single dispatch call.
   The 6 locked layouts (Cover, MarketSize, BusinessModel, Team,
   FinancialProjections, CapitalAsk) live in their own `slidePreviews/*.jsx`
   files and are loaded lazily via `React.lazy` for a smaller initial bundle.

   Header + Footer JSX is constructed here and passed down to the
   gallery layouts as props so every rendered surface shares the
   same eyebrow + title + footer chrome. */

import React, { Suspense, lazy } from "react";
import {
  pillarAccentColor,
  computeBulletSpacing,
} from "@/lib/slideStyleHelpers.jsx";
import { renderGalleryLayout } from "@/components/slidePreviews/GalleryLayouts.jsx";

/* Locked-layout previews — code-split so the initial JS payload for the
 * dashboard/editor first paint doesn't include ~1,100 lines of JSX
 * that only get exercised on specific slides. */
const FinancialProjectionsPreview = lazy(() =>
  import("@/components/slidePreviews/FinancialProjectionsPreview.jsx"));
const CoverPreview = lazy(() =>
  import("@/components/slidePreviews/CoverPreview.jsx"));
const MarketSizePreview = lazy(() =>
  import("@/components/slidePreviews/MarketSizePreview.jsx"));
const BusinessModelPreview = lazy(() =>
  import("@/components/slidePreviews/BusinessModelPreview.jsx"));
const TeamPreview = lazy(() =>
  import("@/components/slidePreviews/TeamPreview.jsx"));
const CapitalAskPreview = lazy(() =>
  import("@/components/slidePreviews/CapitalAskPreview.jsx"));
const CompetitivePreview = lazy(() =>
  import("@/components/slidePreviews/CompetitivePreview.jsx"));

const LazyFallback = () => (
  <div className="h-full w-full" data-print-placeholder="1" style={{ backgroundColor: "#FFFFFF" }} />
);
import { hasMatrix as hasCompetitiveMatrix } from "@/lib/competitive";

const withSuspense = (node) => (
  <Suspense fallback={<LazyFallback />}>{node}</Suspense>
);

export default function SlidePreviewBody({ deck, slide, bullets, primary, layout, onUploadPhoto }) {
  // Locked-layout overrides — slides that always render their canonical
  // design regardless of `design_prefs.layout`. Founders don't see a
  // "Change this slide's design" affordance on any of these.
  const noExtra = !slide?._extraId;
  if (slide?.num === 1 && noExtra) layout = "";
  if (slide?.num === 4 && noExtra) layout = "TAM / SAM / SOM concentric circles";
  if (slide?.num === 5 && noExtra) layout = "Business Strategy 5-row";
  if (slide?.num === 9 && noExtra) layout = "Our Team cards";
  if (slide?.num === 10 && noExtra) layout = "Financial Projections table";
  if (slide?.num === 11 && noExtra) layout = "Capital Ask cards";
  if (slide?.num === 8 && noExtra && hasCompetitiveMatrix(slide?.content)) layout = "Competitive matrix table";

  // Shared spacing + pillar-accent colour used by both the header
  // JSX below and (via `spacing`) every gallery layout that renders
  // bullet columns.
  const spacing = computeBulletSpacing(bullets);
  const pillarAccent = pillarAccentColor(slide?.pillar, primary);

  const Header = (
    <>
      <div className="absolute top-0 left-0 right-0" style={{ height: 4, backgroundColor: pillarAccent }} />
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: pillarAccent }}>
          / {String(slide.num).padStart(2, "0")}{slide.pillar ? ` · ${slide.pillar}` : ""}
        </div>
      </div>
      <div className="mt-2 font-extrabold tracking-tight"
        style={{ color: "#1F2937", fontSize: "clamp(22px, 4vw, 40px)", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
        {slide.title}
      </div>
      {/* Iter84: per-slide sub-question removed from every rendered
          surface. It stays on `slide.question` as editor guidance only. */}
    </>
  );
  const Footer = (
    <div className="pt-2 mt-auto flex items-center justify-between text-[9px]"
      style={{ color: "#9CA3AF", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
      <span className="uppercase tracking-widest">{(deck.company_name || "").toUpperCase()}</span>
      <span className="font-mono">{String(slide.num).padStart(2, "0")} / 12</span>
    </div>
  );

  // Locked-layout dispatch. Each of these lives in its own lazy chunk.
  if (layout.startsWith("Capital Ask"))
    return withSuspense(<CapitalAskPreview deck={deck} slide={slide} primary={primary} />);
  if (layout.startsWith("Competitive matrix"))
    return withSuspense(<CompetitivePreview deck={deck} slide={slide} primary={primary} />);
  if (layout.startsWith("Our Team"))
    return withSuspense(<TeamPreview deck={deck} slide={slide} primary={primary} />);
  if (layout.startsWith("Business Strategy"))
    return withSuspense(<BusinessModelPreview deck={deck} slide={slide} primary={primary} />);
  if (layout.startsWith("TAM"))
    return withSuspense(<MarketSizePreview slide={slide} bullets={bullets} primary={primary} Header={Header} Footer={Footer} />);
  if (!layout && slide.num === 1)
    return withSuspense(<CoverPreview deck={deck} bullets={bullets} primary={primary} />);
  if (layout.startsWith("Financial Projections"))
    return withSuspense(<FinancialProjectionsPreview deck={deck} slide={slide} primary={primary} />);

  // Everything else — design-gallery layouts + default bullet fallback
  // + Thanks (num=12) — all live in GalleryLayouts.jsx.
  return renderGalleryLayout({
    layout, deck, slide, bullets, primary,
    Header, Footer, spacing, onUploadPhoto,
  });
}
